import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Button,
  Alert,
  Tab,
  Tabs,
  Grid,
  Paper,
  Chip,
  TextField,
  IconButton,
} from "@mui/material";
import {
  ArrowLeft,
  Trash2,
  DollarSign,
  BarChart,
  Users,
  Target,
  Zap,
  Edit2,
  Save,
  X,
  TrendingUp,
  Calendar,
  ChevronRight,
  PieChart,
  RefreshCw,
  AlertTriangle,
  MessageSquare,
  Share2,
  Settings,
  Shield,
} from "lucide-react";
import { ALL_PLATFORMS, MOCK_STRATEGY_1 } from "../constants";
import { config } from "../../../../config";
import AdStrategyService from "../../../../services/AdStrategy";
import AIDocumentQA from "../../../../components/AIDocumentQA";
import SendFeedbackDialog from "../../../../components/SendFeedbackDialog";
import { useSelector } from "react-redux";

const DynamicField = React.memo(
  ({ editMode, setEditedData, label, path, value, type = "text" }: any) => {
    if (!editMode) {
      // Regular display
      return (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontSize: "0.75rem",
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          {type === "object" ? (
            // Handle object display
            Object.entries(value).map(([key, val]: any) => (
              <Box key={key} sx={{ ml: 2, mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6B7280",
                    fontWeight: 500,
                  }}
                >
                  {key}:
                </Typography>
                <Typography sx={{ color: "#1F2937", fontWeight: 500 }}>
                  {Array.isArray(val) ? val.join(", ") : val}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ color: "#1F2937", fontWeight: 500 }}>
              {type === "array"
                ? Array.isArray(value)
                  ? value.join(", ")
                  : value
                : value}
            </Typography>
          )}
        </Box>
      );
    }

    // Edit mode
    const handleChange = (type: string, newValue: any, subPath?: string) => {
      if (type === "number") {
        newValue = Number(newValue);
      }

      setEditedData((prev: any) => {
        const updatedData = { ...prev };
        const pathParts = path.split(".");
        let current = updatedData;

        // Create objects along the path if they don't exist
        for (let i = 0; i < pathParts.length - 1; i++) {
          if (!current[pathParts[i]]) {
            current[pathParts[i]] = {};
          }
          current = current[pathParts[i]];
        }

        // Handle object type updates
        if (type === "object" && subPath) {
          if (!current[pathParts[pathParts.length - 1]]) {
            current[pathParts[pathParts.length - 1]] = {};
          }
          current[pathParts[pathParts.length - 1]][subPath] = newValue;
        } else {
          // Handle regular updates
          current[pathParts[pathParts.length - 1]] = newValue;
        }

        return updatedData;
      });
    };

    if (type === "object") {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontSize: "0.75rem",
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          {Object.entries(value).map(([key, val]: any) => (
            <Box key={key} sx={{ ml: 2, mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontWeight: 500,
                }}
              >
                {key}:
              </Typography>
              <TextField
                fullWidth
                value={Array.isArray(val) ? val.join(", ") : val}
                onChange={(e) =>
                  handleChange(
                    type,
                    Array.isArray(val)
                      ? e.target.value.split(",").map((s) => s.trim())
                      : e.target.value,
                    key
                  )
                }
                size="small"
                placeholder={`Enter ${key}`}
                sx={{
                  mt: 0.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "rgba(0,0,0,0.1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0,0,0,0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#DC2626",
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      );
    }

    if (type === "array") {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontSize: "0.75rem",
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <TextField
            fullWidth
            value={Array.isArray(value) ? value.join(", ") : value}
            onChange={(e) =>
              handleChange(
                type,
                e.target.value.split(",").map((s) => s.trim())
              )
            }
            size="small"
            placeholder="Comma-separated values"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#DC2626",
                  borderWidth: "1px",
                },
              },
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "#6B7280",
            fontSize: "0.75rem",
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <TextField
          fullWidth
          value={value}
          onChange={(e) => handleChange(type, e.target.value)}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "rgba(0,0,0,0.1)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(0,0,0,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#DC2626",
                borderWidth: "1px",
              },
            },
          }}
        />
      </Box>
    );
  }
);

const AdStrategyDetailView = () => {
  const { projectId, strategyId } = useParams();
  const reduxUser = useSelector((state: any) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState<any>(() => {
    if (!config.inProd) {
      return MOCK_STRATEGY_1;
    }
    return location.state?.strategy || null;
  });
  const [selectedPlatform, setSelectedPlatform] = useState("overview");
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState<any>(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const newAdStrategyServiceInstance = useMemo(
    () => new AdStrategyService(),
    []
  );

  useEffect(() => {
    if (config.inProd && strategy === null) {
      fetchStrategyData();
    }
  }, []);

  const fetchStrategyData = async () => {
    setLoading(true);
    try {
      const adStrategyService = newAdStrategyServiceInstance;
      const strategy = await adStrategyService.getStrategy(
        projectId,
        strategyId
      );
      console.log("strategy:", strategy);

      setStrategy(strategy);
    } catch (error: Error | any) {
      setError("Failed to load strategy details:");
      console.log("fetchStrategyData error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Are you sure you want to delete this strategy?"))
      return;

    if (!config.inProd) {
      navigate(`/project/${projectId}/adstrategy`);
      return;
    }

    try {
      const adStrategyService = newAdStrategyServiceInstance;
      await adStrategyService.deleteStrategy(projectId, strategyId);
      navigate(`/project/${projectId}/adstrategy`);
    } catch (error: Error | any) {
      setError("Failed to delete strategy");
      console.log("delete error:", error);
    }
  }, [projectId, navigate]);

  const handleEditToggle = useCallback(() => {
    if (editMode) {
      setEditedData(null);
    } else {
      if (selectedPlatform === "overview") {
        // Handle overview mode initialization
        const fullPlatforms = Object.entries(ALL_PLATFORMS).reduce(
          (acc, [key, platformConfig]: any) => ({
            ...acc,
            [key]: strategy.platforms[key] || {
              name: platformConfig.name,
              enabled: false,
              configuration: platformConfig.configuration,
              metrics: {
                budget: 0,
                spend: 0,
                impressions: 0,
                clicks: 0,
                conversions: 0,
                costPerClick: 0,
                costPerConversion: 0,
                conversionRate: 0,
              },
            },
          }),
          {}
        );

        setEditedData({
          ...strategy,
          platforms: fullPlatforms,
        });
      } else {
        // Handle platform-specific view initialization
        setEditedData({
          platforms: {
            [selectedPlatform]: {
              ...strategy.platforms[selectedPlatform],
              metrics: {
                ...strategy.platforms[selectedPlatform].metrics,
              },
              campaign: {
                ...strategy.platforms[selectedPlatform].campaign,
              },
              targeting: {
                ...strategy.platforms[selectedPlatform].targeting,
              },
            },
          },
        });
      }
    }
    setEditMode(!editMode);
  }, [selectedPlatform, strategy.platforms, editMode]);

  const handleSaveChanges = useCallback(async () => {
    if (!config.inProd) {
      setStrategy((prev: any) => ({
        ...prev,
        platforms: {
          ...prev.platforms,
          [selectedPlatform]: editedData,
        },
      }));
      setEditMode(false);
      setEditedData(null);
      return;
    }

    try {
      const adStrategyService = newAdStrategyServiceInstance;

      await adStrategyService.updateStrategy(projectId, strategyId, {
        platforms: {
          ...strategy.platforms,
          [selectedPlatform]: editedData.platforms[selectedPlatform],
        },
      });

      // Refresh strategy data
      const updatedStrategy = await adStrategyService.getStrategy(
        projectId,
        strategyId
      );
      setStrategy(updatedStrategy);
      setEditMode(false);
      setEditedData(null);
    } catch (error: Error | any) {
      setError("Failed to save changes");
      console.log("saving error:", error);
    }
  }, [projectId, strategyId, selectedPlatform, strategy.platforms, editedData]);

  const renderPlatformMetrics = (metrics: any) => {
    const primaryMetrics = [
      {
        key: "suggestedBudget",
        label: "Budget",
        prefix: "$",
        icon: DollarSign,
      },
      { key: "estimatedReach", label: "Est. Reach", icon: Users },
      { key: "estimatedCPC", label: "Est. CPC", prefix: "$", icon: TrendingUp },
    ];

    return (
      <Grid container spacing={3}>
        {primaryMetrics.map((metric) => (
          <Grid item xs={4} key={metric.key}>
            {editMode ? (
              <DynamicField
                label={metric.label}
                path={`platforms.${selectedPlatform}.metrics.${metric.key}`}
                value={
                  editedData?.platforms?.[selectedPlatform]?.metrics?.[
                    metric.key
                  ]
                }
                type="number"
                editMode={editMode}
                setEditedData={setEditedData}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.15))",
                    flexShrink: 0,
                  }}
                >
                  <metric.icon className="h-4 w-4 text-red-600" />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                    }}
                  >
                    {metric.prefix || ""}
                    {metrics[metric.key]
                      ? metrics[metric.key].toLocaleString()
                      : "0"}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderPlatformDetails = (platform: any, data: any) => {
    if (!data) return null;

    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              textTransform: "capitalize",
              fontWeight: 600,
              color: "#1F2937",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                boxShadow: "0 2px 5px rgba(220, 38, 38, 0.1)",
              }}
            >
              <Target className="h-5 w-5 text-red-600" />
            </Box>
            {platform.replace("Ads", "")} Settings
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>{renderEditControls()}</Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid rgba(0,0,0,0.05)",
                  background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1F2937",
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <BarChart className="h-5 w-5 text-red-600" />
                  Performance Metrics
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>{renderPlatformMetrics(data.metrics)}</Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid rgba(0,0,0,0.05)",
                  background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1F2937",
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Zap className="h-5 w-5 text-red-600" />
                  Campaign Setup
                </Typography>
                <Typography sx={{ color: "#6B7280", fontSize: "0.9rem" }}>
                  Configuration and goals
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  {editMode ? (
                    <DynamicField
                      label="Objective"
                      path={`platforms.${selectedPlatform}.campaign.objective`}
                      value={
                        editedData?.platforms?.[selectedPlatform]?.campaign
                          ?.objective
                      }
                      editMode={editMode}
                      setEditedData={setEditedData}
                    />
                  ) : (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#6B7280",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        Objective
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1F2937",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Target className="h-4 w-4 text-red-600" />
                        {data.campaign?.objective || data.campaign?.type}
                      </Typography>
                    </>
                  )}
                </Box>
                {data.campaign?.goals && (
                  <Box sx={{ mb: 2 }}>
                    {editMode ? (
                      <DynamicField
                        label="Goals"
                        path={`platforms.${selectedPlatform}.campaign.goals`}
                        value={
                          editedData?.platforms?.[selectedPlatform]?.campaign
                            ?.goals
                        }
                        type="array"
                        editMode={editMode}
                        setEditedData={setEditedData}
                      />
                    ) : (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#6B7280",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          Goals
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            flexWrap: "wrap",
                          }}
                        >
                          {data.campaign.goals.map(
                            (goal: string, i: number) => (
                              <Chip
                                key={i}
                                label={goal}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  backgroundColor: "rgba(220, 38, 38, 0.1)",
                                  color: "#B91C1C",
                                  borderRadius: "12px",
                                  mb: 0.75,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "rgba(220, 38, 38, 0.15)",
                                    boxShadow:
                                      "0 2px 4px rgba(220, 38, 38, 0.1)",
                                  },
                                }}
                              />
                            )
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid rgba(0,0,0,0.05)",
                  background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1F2937",
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Users className="h-5 w-5 text-red-600" />
                  Targeting
                </Typography>
                <Typography sx={{ color: "#6B7280", fontSize: "0.9rem" }}>
                  Audience and targeting settings
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                {data.targeting &&
                  Object.entries(data.targeting).map(([key, value]: any) => (
                    <Box key={key} sx={{ mb: 3 }}>
                      {editMode ? (
                        <DynamicField
                          label={key.replace(/([A-Z])/g, " $1").trim()}
                          path={`platforms.${selectedPlatform}.targeting.${key}`}
                          value={
                            editedData?.platforms?.[selectedPlatform]
                              ?.targeting?.[key]
                          }
                          type={
                            typeof value === "object" && !Array.isArray(value)
                              ? "object"
                              : "array"
                          }
                          editMode={editMode}
                          setEditedData={setEditedData}
                        />
                      ) : (
                        <>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: "#6B7280",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 1,
                              textTransform: "capitalize",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "4px",
                                backgroundColor: "rgba(220, 38, 38, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Target className="h-3 w-3 text-red-600" />
                            </Box>
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </Typography>
                          {Array.isArray(value) && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.75,
                                flexWrap: "wrap",
                              }}
                            >
                              {value.map((item: string, i: number) => (
                                <Chip
                                  key={i}
                                  label={item}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    backgroundColor: "#F3F4F6",
                                    color: "#4B5563",
                                    borderRadius: "12px",
                                    mb: 0.75,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "#E5E7EB",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderEditControls = () => (
    <Box sx={{ display: "flex", gap: 1 }}>
      {editMode ? (
        <>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveChanges}
            sx={{
              minWidth: 100,
              background: "linear-gradient(to right, #DC2626, #EF4444)",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(220, 38, 38, 0.25)",
              "&:hover": {
                background: "linear-gradient(to right, #B91C1C, #DC2626)",
                boxShadow: "0 3px 6px rgba(220, 38, 38, 0.3)",
              },
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<X />}
            onClick={() => {
              setEditMode(false);
              setEditedData(null);
            }}
            sx={{
              minWidth: 100,
              borderColor: "rgba(107, 114, 128, 0.3)",
              color: "#4B5563",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              "&:hover": {
                borderColor: "rgba(107, 114, 128, 0.5)",
                backgroundColor: "rgba(249, 250, 251, 0.8)",
              },
            }}
          >
            Cancel
          </Button>
        </>
      ) : (
        <Button
          variant="outlined"
          startIcon={<Edit2 size={16} />}
          onClick={handleEditToggle}
          sx={{
            minWidth: 100,
            borderColor: "rgba(107, 114, 128, 0.3)",
            color: "#4B5563",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "8px",
            "&:hover": {
              borderColor: "rgba(107, 114, 128, 0.5)",
              backgroundColor: "rgba(249, 250, 251, 0.8)",
            },
          }}
        >
          Edit
        </Button>
      )}
    </Box>
  );

  const renderOverview = () => {
    const allPlatforms = Object.entries(strategy.platforms || {});
    const totalMetrics = allPlatforms.reduce(
      (acc, [_, platform]: any) => ({
        budget: acc.budget + (platform.metrics?.suggestedBudget || 0),
        reach: acc.reach + (platform.metrics?.estimatedReach || 0),
        conversions: acc.conversions + (platform.metrics?.conversions || 0),
        spend: acc.spend + (platform.metrics?.spend || 0),
      }),
      { budget: 0, reach: 0, conversions: 0, spend: 0 }
    );

    const overviewMetrics = [
      {
        label: "Total Budget",
        value: totalMetrics.budget.toLocaleString(),
        prefix: "$",
        icon: DollarSign,
        color:
          "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
        iconColor: "text-red-600",
      },
      {
        label: "Estimated Reach",
        value: totalMetrics.reach.toLocaleString(),
        icon: Users,
        color:
          "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
        iconColor: "text-red-600",
      },
    ];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1F2937",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                    boxShadow: "0 2px 5px rgba(220, 38, 38, 0.1)",
                  }}
                >
                  <BarChart className="h-5 w-5 text-red-600" />
                </Box>
                Strategy Summary
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {overviewMetrics.map((metric, index) => (
                <Grid item xs={12} md={3} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 2,
                      borderRadius: "10px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(249, 250, 251, 0.8)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: metric.color,
                        boxShadow: "0 3px 6px rgba(220, 38, 38, 0.1)",
                        flexShrink: 0,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                        },
                      }}
                    >
                      <metric.icon className={`h-5 w-5 ${metric.iconColor}`} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {metric.label}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1F2937",
                          fontSize: "1.2rem",
                        }}
                      >
                        {metric.prefix || ""}
                        {metric.value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "8px",
                background: "linear-gradient(to right, #F9FAFB, #F3F4F6)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1F2937",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                  }}
                >
                  <PieChart className="h-4 w-4 text-red-600" />
                </Box>
                Platform Distribution
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {allPlatforms.map(
                ([platformKey, platform]: any, index: number) => (
                  <Grid item xs={12} sm={6} md={4} key={platformKey}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        border: "1px solid rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                          borderColor: "rgba(220, 38, 38, 0.2)",
                        },
                      }}
                      onClick={() => setSelectedPlatform(platformKey)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(135deg, rgba(220, 38, 38, 0.05), rgba(220, 38, 38, 0.15))",
                          }}
                        >
                          <Target className="h-4 w-4 text-red-600" />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#1F2937" }}
                        >
                          {platform.name || platformKey.replace("Ads", "")}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          Budget:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          $
                          {platform.metrics?.suggestedBudget?.toLocaleString() ||
                            "0"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          Est. Reach:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {platform.metrics?.estimatedReach?.toLocaleString() ||
                            "0"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          Est. CPC:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          $
                          {platform.metrics?.estimatedCPC?.toLocaleString() ||
                            "0"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "#DC2626",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        >
                          View Details <ChevronRight className="h-3 w-3" />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )
              )}
            </Grid>
          </Paper>
        </Grid>
        {/* Email Strategy */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              overflow: "hidden",
              mb: 3,
            }}
          >
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "8px",
                background: "linear-gradient(to right, #F9FAFB, #F3F4F6)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1F2937",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                  }}
                >
                  <MessageSquare className="h-4 w-4 text-red-600" />
                </Box>
                Email Marketing Strategy
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Overall Strategy */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Target className="h-4 w-4 text-red-600" />
                    Campaign Objective
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563", mb: 2 }}>
                    {strategy.emailStrategy?.overallStrategy?.objective}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Key Performance Indicators
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.emailStrategy?.overallStrategy?.keyPerformanceIndicators?.map(
                      (kpi: any, i: number) => (
                        <Chip
                          key={i}
                          label={kpi}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(220, 38, 38, 0.15)",
                              boxShadow: "0 2px 4px rgba(220, 38, 38, 0.1)",
                            },
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Target Segments
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.emailStrategy?.overallStrategy?.targetSegments?.map(
                      (segment: any, i: number) => (
                        <Chip
                          key={i}
                          label={segment}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#E5E7EB",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            },
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Automation Workflows */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <RefreshCw className="h-4 w-4 text-red-600" />
                    Automation Workflows
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    {strategy.emailStrategy?.automationWorkflows
                      ?.slice(0, 2)
                      .map((workflow: any, i: number) => (
                        <Box
                          key={i}
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: "8px",
                            backgroundColor: "rgba(249, 250, 251, 0.8)",
                            border: "1px solid rgba(0,0,0,0.03)",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            {workflow.trigger} Trigger
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "#6B7280", mb: 1 }}
                          >
                            {workflow.emailSequence?.[0]?.subjectLine}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "#DC2626",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              justifyContent: "flex-end",
                            }}
                          >
                            {workflow.emailSequence?.length || 0} emails in
                            sequence <ChevronRight className="h-3 w-3" />
                          </Box>
                        </Box>
                      ))}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Content Types
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.emailStrategy?.contentStrategy?.emailTypes?.map(
                      (type: any, i: number) => (
                        <Chip
                          key={i}
                          label={type}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#E5E7EB",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            },
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Personalization & Analytics */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Users className="h-4 w-4 text-red-600" />
                    Personalization Strategy
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Data Points Used
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.emailStrategy?.personalizationTactics?.dataPointsUsed?.map(
                      (dataPoint: any, i: number) => (
                        <Chip
                          key={i}
                          label={dataPoint}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    AI-Driven Personalization
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563", mb: 2 }}>
                    {
                      strategy.emailStrategy?.personalizationTactics
                        ?.aiDrivenPersonalization
                    }
                  </Typography>
                </Box>
              </Grid>

              {/* Testing & Optimization */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <BarChart className="h-4 w-4 text-red-600" />
                    Testing & Optimization
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    A/B Testing Strategies
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.emailStrategy?.analyticsAndOptimization?.aBTestingStrategies?.map(
                      (strategy: any, i: number) => (
                        <Chip
                          key={i}
                          label={strategy}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Reporting Frequency
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563", mb: 1 }}>
                    {
                      strategy.emailStrategy?.analyticsAndOptimization
                        ?.reportingFrequency
                    }
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Emerging Trends
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.emailStrategy?.emergingTrends?.map(
                      (trend: any, i: number) => (
                        <Chip
                          key={i}
                          label={trend}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Influencer Marketing Strategy */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              overflow: "hidden",
              mb: 3,
            }}
          >
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "8px",
                background: "linear-gradient(to right, #F9FAFB, #F3F4F6)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1F2937",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                  }}
                >
                  <Users className="h-4 w-4 text-red-600" />
                </Box>
                Influencer Marketing Strategy
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Strategy Overview */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Target className="h-4 w-4 text-red-600" />
                    Campaign Strategy
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563", mb: 2 }}>
                    {strategy.influencerMarketing?.strategy?.contentApproach}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Platform Mix
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.influencerMarketing?.strategy?.platformMix?.map(
                      (platform: any, i: number) => (
                        <Chip
                          key={i}
                          label={platform}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(220, 38, 38, 0.15)",
                              boxShadow: "0 2px 4px rgba(220, 38, 38, 0.1)",
                            },
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Influencer Tiers */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Users className="h-4 w-4 text-red-600" />
                    Influencer Distribution
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#6B7280",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        mb: 0.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Micro Influencers (
                      {
                        strategy.influencerMarketing?.influencerTiers?.micro
                          ?.followerRange
                      }
                      )
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                          {
                            strategy.influencerMarketing?.influencerTiers?.micro
                              ?.quantity
                          }
                        </span>{" "}
                        creators
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Est. Reach:{" "}
                        <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                          {strategy.influencerMarketing?.influencerTiers?.micro?.estimatedReach?.toLocaleString()}
                        </span>
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#6B7280",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        mb: 0.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Macro Influencers (
                      {
                        strategy.influencerMarketing?.influencerTiers?.macro
                          ?.followerRange
                      }
                      )
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                          {
                            strategy.influencerMarketing?.influencerTiers?.macro
                              ?.quantity
                          }
                        </span>{" "}
                        creators
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Est. Reach:{" "}
                        <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                          {strategy.influencerMarketing?.influencerTiers?.macro?.estimatedReach?.toLocaleString()}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Content Guidelines */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <RefreshCw className="h-4 w-4 text-red-600" />
                    Content Framework
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Content Formats
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.influencerMarketing?.contentGuidelines?.formats?.map(
                      (format: any, i: number) => (
                        <Chip
                          key={i}
                          label={format}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Key Messages
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {strategy.influencerMarketing?.contentGuidelines?.keyMessages?.map(
                      (message: any, i: number) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{ color: "#4B5563", mb: 1 }}
                        >
                          • {message}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Performance Metrics */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <BarChart className="h-4 w-4 text-red-600" />
                    Performance Targets
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#6B7280",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      Engagement Benchmarks
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Micro:{" "}
                        <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                          {
                            strategy.influencerMarketing?.metrics
                              ?.engagementBenchmarks?.micro
                          }
                          %
                        </span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Macro:{" "}
                        <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                          {
                            strategy.influencerMarketing?.metrics
                              ?.engagementBenchmarks?.macro
                          }
                          %
                        </span>
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Primary KPIs
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.influencerMarketing?.metrics?.primaryKPIs?.map(
                      (kpi: any, i: number) => (
                        <Chip
                          key={i}
                          label={kpi}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Affiliate Marketing Program */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              overflow: "hidden",
              mb: 3,
            }}
          >
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "8px",
                background: "linear-gradient(to right, #F9FAFB, #F3F4F6)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1F2937",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                  }}
                >
                  <Share2 className="h-4 w-4 text-red-600" />
                </Box>
                Affiliate Marketing Program
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Program Structure */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Users className="h-4 w-4 text-red-600" />
                    Program Tiers
                  </Typography>

                  {strategy.affiliateMarketing?.program?.partnerTiers?.map(
                    (tier: any, i: number) => (
                      <Box
                        key={i}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: "rgba(249, 250, 251, 0.8)",
                          border: "1px solid rgba(0,0,0,0.03)",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                        >
                          {tier.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "#6B7280", mb: 1 }}
                        >
                          Commission: {tier.commissionRates.firstSale}% first
                          sale, {tier.commissionRates.recurring}% recurring
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            flexWrap: "wrap",
                          }}
                        >
                          {tier.benefits.map((benefit: any, j: number) => (
                            <Chip
                              key={j}
                              label={benefit}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                backgroundColor: "#F3F4F6",
                                color: "#4B5563",
                                borderRadius: "12px",
                                mb: 0.75,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              </Grid>

              {/* Partner Types */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Target className="h-4 w-4 text-red-600" />
                    Partner Categories
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#1F2937",
                        mb: 1,
                        fontSize: "0.85rem",
                      }}
                    >
                      Content Creators
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        flexWrap: "wrap",
                        mb: 2,
                      }}
                    >
                      {strategy.affiliateMarketing?.partnerTypes?.contentCreators?.platforms?.map(
                        (platform: any, i: number) => (
                          <Chip
                            key={i}
                            label={platform}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              backgroundColor: "rgba(220, 38, 38, 0.1)",
                              color: "#B91C1C",
                              borderRadius: "12px",
                              mb: 0.75,
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#1F2937",
                        mb: 1,
                        fontSize: "0.85rem",
                      }}
                    >
                      Industry Experts
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      {strategy.affiliateMarketing?.partnerTypes?.industryExperts?.niches?.map(
                        (niche: any, i: number) => (
                          <Chip
                            key={i}
                            label={niche}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              backgroundColor: "#F3F4F6",
                              color: "#4B5563",
                              borderRadius: "12px",
                              mb: 0.75,
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#1F2937",
                        mb: 1,
                        fontSize: "0.85rem",
                      }}
                    >
                      Media Partners
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      {strategy.affiliateMarketing?.partnerTypes?.mediaPartners?.types?.map(
                        (type: any, i: number) => (
                          <Chip
                            key={i}
                            label={type}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              backgroundColor: "#F3F4F6",
                              color: "#4B5563",
                              borderRadius: "12px",
                              mb: 0.75,
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Performance Metrics */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <BarChart className="h-4 w-4 text-red-600" />
                    Revenue Targets
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: "8px",
                      backgroundColor: "rgba(249, 250, 251, 0.8)",
                      border: "1px solid rgba(0,0,0,0.03)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        Quarterly Target:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#1F2937" }}
                      >
                        $
                        {strategy.affiliateMarketing?.metrics?.revenueTargets?.quarterly?.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        Annual Target:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#1F2937" }}
                      >
                        $
                        {strategy.affiliateMarketing?.metrics?.revenueTargets?.annual?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Key Performance Indicators
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.affiliateMarketing?.metrics?.kpis?.map(
                      (kpi: any, i: number) => (
                        <Chip
                          key={i}
                          label={kpi}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Technology */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Settings className="h-4 w-4 text-red-600" />
                    Technology Stack
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Tracking Platform
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563", mb: 2 }}>
                    {strategy.affiliateMarketing?.technology?.trackingPlatform}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Integrations
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.affiliateMarketing?.technology?.integrations?.map(
                      (integration: any, i: number) => (
                        <Chip
                          key={i}
                          label={integration}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Compliance */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Shield className="h-4 w-4 text-red-600" />
                    Compliance & Guidelines
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Disclosure Requirements
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.affiliateMarketing?.compliance?.disclosureRequirements?.map(
                      (requirement: any, i: number) => (
                        <Chip
                          key={i}
                          label={requirement}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Restricted Practices
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.affiliateMarketing?.compliance?.restrictedPractices?.map(
                      (practice: any, i: number) => (
                        <Chip
                          key={i}
                          label={practice}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Growth Strategy */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "8px",
                background: "linear-gradient(to right, #F9FAFB, #F3F4F6)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1F2937",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                  }}
                >
                  <TrendingUp className="h-4 w-4 text-red-600" />
                </Box>
                Growth Strategy
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Core Strategy */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Target className="h-4 w-4 text-red-600" />
                    Core Strategy
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    North Star Metric
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#1F2937", fontWeight: 500, mb: 2 }}
                  >
                    {strategy.growthStrategy?.core?.northStarMetric}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Growth Levers
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.growthStrategy?.core?.growthLevers?.map(
                      (lever: any, i: number) => (
                        <Chip
                          key={i}
                          label={lever}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Experimentation Framework
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563", mb: 1 }}>
                    {strategy.growthStrategy?.core?.experimentationFramework}
                  </Typography>
                </Box>
              </Grid>

              {/* Acquisition Strategy */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Users className="h-4 w-4 text-red-600" />
                    Acquisition Channels
                  </Typography>

                  {strategy.growthStrategy?.acquisition?.viralLoops?.map(
                    (loop: any, i: number) => (
                      <Box
                        key={i}
                        sx={{
                          mb: 3,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: "rgba(249, 250, 251, 0.8)",
                          border: "1px solid rgba(0,0,0,0.03)",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                        >
                          Viral Loop #{i + 1}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#4B5563", mb: 1 }}
                        >
                          {loop.mechanism}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#6B7280" }}
                          >
                            Type:{" "}
                            <span style={{ fontWeight: 500 }}>{loop.type}</span>
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#6B7280" }}
                          >
                            Expected K-factor:{" "}
                            <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                              {loop.expectedK}
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Community Growth Platforms
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.growthStrategy?.acquisition?.communityGrowth?.platforms?.map(
                      (platform: any, i: number) => (
                        <Chip
                          key={i}
                          label={platform}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Product-Led Growth */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Zap className="h-4 w-4 text-red-600" />
                    Product-Led Growth
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Aha Moments
                  </Typography>
                  <Box sx={{ pl: 2, mb: 3 }}>
                    {strategy.growthStrategy?.acquisition?.productLedGrowth?.ahaMoments?.map(
                      (moment: any, i: number) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{ color: "#4B5563", mb: 0.75 }}
                        >
                          • {moment}
                        </Typography>
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Product Hooks
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {strategy.growthStrategy?.acquisition?.productLedGrowth?.hooks?.map(
                      (hook: any, i: number) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{ color: "#4B5563", mb: 0.75 }}
                        >
                          • {hook}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Retention Strategy */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <RefreshCw className="h-4 w-4 text-red-600" />
                    Retention Strategy
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Loyalty Program Structure
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#1F2937", fontWeight: 500, mb: 2 }}
                  >
                    {
                      strategy.growthStrategy?.retention?.loyaltyProgram
                        ?.structure
                    }
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Loyalty Program Tiers
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.growthStrategy?.retention?.loyaltyProgram?.tiers?.map(
                      (tier: any, i: number) => (
                        <Chip
                          key={i}
                          label={tier}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Reengagement Triggers
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.growthStrategy?.retention?.reengagementTriggers?.map(
                      (trigger: any, i: number) => (
                        <Chip
                          key={i}
                          label={trigger}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Monetization */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DollarSign className="h-4 w-4 text-red-600" />
                    Monetization Strategy
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Pricing Strategy
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#1F2937", fontWeight: 500, mb: 2 }}
                  >
                    {strategy.growthStrategy?.monetization?.pricingStrategy}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Expansion Revenue
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.growthStrategy?.monetization?.expansionRevenue?.map(
                      (item: any, i: number) => (
                        <Chip
                          key={i}
                          label={item}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Experimentation */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1F2937",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <BarChart className="h-4 w-4 text-red-600" />
                    Experimentation Framework
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Framework
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#1F2937", fontWeight: 500, mb: 2 }}
                  >
                    {strategy.growthStrategy?.experimentation?.framework}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Testing Priorities
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {strategy.growthStrategy?.experimentation?.testingPriorities?.map(
                      (priority: any, i: number) => (
                        <Chip
                          key={i}
                          label={priority}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#6B7280",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    Success Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {strategy.growthStrategy?.experimentation?.successMetrics?.map(
                      (metric: any, i: number) => (
                        <Chip
                          key={i}
                          label={metric}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            color: "#B91C1C",
                            borderRadius: "12px",
                            mb: 0.75,
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: "1400px", mx: "auto" }}>
      {/* Loading and error states */}
      {loading && (
        <Box
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "12px",
            background: "#F9FAFB",
            minHeight: "200px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(220, 38, 38, 0.1)",
              }}
            >
              <RefreshCw className="h-6 w-6 text-red-600 animate-spin" />
            </Box>
            <Typography sx={{ fontWeight: 500, color: "#4B5563" }}>
              Loading strategy data...
            </Typography>
          </Box>
        </Box>
      )}

      {!loading && !strategy && (
        <Box
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "12px",
            background: "#F9FAFB",
            border: "1px dashed rgba(0,0,0,0.1)",
            minHeight: "200px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(220, 38, 38, 0.1)",
              }}
            >
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </Box>
            <Typography sx={{ fontWeight: 500, color: "#4B5563" }}>
              Strategy not found
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate(`/project/${projectId}/adstrategy`)}
              sx={{
                mt: 2,
                borderColor: "rgba(107, 114, 128, 0.3)",
                color: "#4B5563",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "rgba(107, 114, 128, 0.5)",
                  backgroundColor: "rgba(249, 250, 251, 0.8)",
                },
              }}
            >
              Back to Strategies
            </Button>
          </Box>
        </Box>
      )}

      {!loading && strategy && (
        <>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/project/${projectId}/adstrategy`)}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  borderColor: "rgba(0,0,0,0.1)",
                  color: "#4B5563",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "rgba(0,0,0,0.2)",
                    bgcolor: "rgba(243, 244, 246, 0.8)",
                  },
                }}
              >
                <ArrowLeft size={20} />
              </Button>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#111827" }}
              >
                {strategy.name || "Ad Strategy Details"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<MessageSquare size={18} />}
                onClick={() => setShowFeedbackDialog(true)}
                sx={{
                  borderColor: "rgba(107, 114, 128, 0.3)",
                  color: "#4B5563",
                  textTransform: "none",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "rgba(107, 114, 128, 0.5)",
                    backgroundColor: "rgba(249, 250, 251, 0.8)",
                  },
                }}
              >
                Feedback
              </Button>
              <Button
                variant="outlined"
                startIcon={<Trash2 size={18} />}
                onClick={handleDelete}
                sx={{
                  borderColor: "rgba(239, 68, 68, 0.5)",
                  color: "#EF4444",
                  textTransform: "none",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "#EF4444",
                    bgcolor: "rgba(239, 68, 68, 0.04)",
                  },
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>

          <Card
            sx={{
              mb: 3,
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              border: "1px solid rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid rgba(0,0,0,0.05)",
                background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "#111827", mb: 1 }}
                  >
                    {strategy.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "6px",
                        bgcolor: "rgba(220, 38, 38, 0.05)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Calendar className="h-4 w-4 text-red-600" />
                      <Typography
                        variant="body2"
                        sx={{ color: "#4B5563", fontWeight: 500 }}
                      >
                        Created{" "}
                        {new Date(strategy.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "6px",
                        bgcolor: "rgba(107, 114, 128, 0.05)",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        ID: {strategy._id || strategy.id}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <AIDocumentQA
                  userId={reduxUser.id}
                  documentName={strategy.name}
                  projectId={projectId}
                  documentId={strategyId}
                  documentType="Strategy"
                  service={newAdStrategyServiceInstance}
                />
              </Box>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  m: 3,
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
                action={
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={() => setError(null)}
                  >
                    <X />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            )}

            {/* Platform tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                overflowX: "auto", // Enable horizontal scrolling
                width: "100%", // Ensure full width
              }}
            >
              <Tabs
                value={selectedPlatform}
                onChange={(_, newValue) => setSelectedPlatform(newValue)}
                sx={{
                  px: 2,
                  minWidth: "max-content", // Ensure tabs don't compress
                  "& .MuiTabs-scroller": {
                    overflowX: "auto", // Additional scrolling support
                  },
                }}
                variant="scrollable" // Enable scrolling
                scrollButtons="auto" // Show scroll buttons when needed
              >
                <Tab value="overview" label="Overview" />
                {Object.keys(strategy.platforms).map((platform) => (
                  <Tab
                    key={platform}
                    value={platform}
                    label={platform.replace("Ads", "")}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Selected platform content */}
            <Box sx={{ p: 3 }}>
              {selectedPlatform === "overview"
                ? renderOverview()
                : renderPlatformDetails(
                    selectedPlatform,
                    strategy.platforms?.[selectedPlatform]
                  )}
            </Box>
          </Card>
          <SendFeedbackDialog
            open={showFeedbackDialog}
            onClose={() => setShowFeedbackDialog(false)}
            userId={reduxUser.id}
            projectId={projectId}
            documentId={strategyId}
            documentType="adstrategy"
            documentName={strategy?.name || "Ad Strategy"}
          />
        </>
      )}
    </Box>
  );
};

export default AdStrategyDetailView;
